import { useMemo } from 'react';

import { STEP_CATALOGUE_GC_PAGE_SIZE } from 'constants/appConstants';
import {
  REGULAR_COLLECTIONS,
  SOILINPUT_COLLECTION,
} from 'constants/gcConstants';
import {
  G_SOILINPUT_QUERY_KEY,
  M_SEARCH_SOILINPUT_QUERY_KEY,
  M_SOILINPUT_QUERY_KEY,
} from 'constants/queryKeys';
import { useCatalogueDropdown } from 'hooks/useCatalogueDropdown';
import { useMOrgId } from 'hooks/useMOrgId';
import { GSoilInput } from 'interfaces/IGlobalCatalogue';
import { MSoilInput } from 'interfaces/IMasterDb';
import { composeList } from 'utils/catalogueMergeUtils';

import { StepSelectorOption } from './stepConfig';

const toStepSelectorOption = (item: GSoilInput): StepSelectorOption => ({
  label: item.name,
  item: item.name,
  globalId: item.globalId,
  rate: item.rate ?? null,
  cost: item.price ?? null,
  unit: item.rateUnit ?? null,
  techStackId: item.techStackId ?? null,
  snapTechStack: item.snapTechStack ?? null,
  productGroups: item.productGroups,
  isSeedDressing: item.isSeedDressing,
  co2eScope1: item.co2eScope1 ?? null,
  co2eScope2: item.co2eScope2 ?? null,
  co2eScope3: item.co2eScope3 ?? null,
  co2eUnit: item.co2eUnit ?? null,
  manufacturerId: item.manufacturerId ?? null,
  snapManufacturer: item.snapManufacturer ?? null,
  supplierId: item.supplierId ?? null,
  snapSupplier: item.snapSupplier ?? null,
  additive: item.additive ?? null,
  nutrientN: item.nutrientN ?? null,
  nutrientP: item.nutrientP ?? null,
  nutrientK: item.nutrientK ?? null,
  nutrientS: item.nutrientS ?? null,
  nutrientCa: item.nutrientCa ?? null,
  nutrientMg: item.nutrientMg ?? null,
  nutrientNa: item.nutrientNa ?? null,
  nutrientB: item.nutrientB ?? null,
  nutrientCu: item.nutrientCu ?? null,
  nutrientFe: item.nutrientFe ?? null,
  nutrientMn: item.nutrientMn ?? null,
  nutrientZn: item.nutrientZn ?? null,
  nutrientMo: item.nutrientMo ?? null,
  nutrientCl: item.nutrientCl ?? null,
  nutrientSi: item.nutrientSi ?? null,
  nutrientNi: item.nutrientNi ?? null,
  nutrientCo: item.nutrientCo ?? null,
});

export const useSoilInputCatalogue = (gOrgId: string | null) => {
  const mOrgId = useMOrgId();

  const {
    items,
    isLoading,
    isSearchFetching,
    isPaginating: isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    hasFetchNextPageError,
    searchTerm,
    onSearch,
    clearSearch,
  } = useCatalogueDropdown<GSoilInput, MSoilInput, GSoilInput>({
    gOrgId,
    mOrgId,
    gCollectionKey: SOILINPUT_COLLECTION,
    mCollectionKey: SOILINPUT_COLLECTION,
    gSearchCollectionName: REGULAR_COLLECTIONS.soilInput,
    gQueryKey: [G_SOILINPUT_QUERY_KEY],
    mQueryKey: [M_SOILINPUT_QUERY_KEY],
    searchQueryKey: [M_SEARCH_SOILINPUT_QUERY_KEY],
    mergeItems: composeList<GSoilInput, MSoilInput>,
    gPageSize: STEP_CATALOGUE_GC_PAGE_SIZE,
  });

  const soilInputOptions = useMemo(
    () => items.map(toStepSelectorOption),
    [items]
  );

  return {
    soilInputOptions,
    isLoading,
    isSearchFetching,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    hasFetchNextPageError,
    searchTerm,
    onSearch,
    clearSearch,
  };
};
